"use client"

import { useMemo } from "react"

import {
  resolveTenantAuthorizationModuleAction,
} from "@/features/auth/tenant-authorization/lib/tenant-authorization-registry"
import {
  TenantAuthorizationAction,
  TenantAuthorizationDecisionStatus,
} from "@/features/auth/tenant-authorization/model/tenant-authorization.types"
import {
  WorkspaceAccessModule,
  WorkspaceAccessPermission,
} from "@/features/auth/model/auth-types"
import { useTenantAuthorization } from "@/features/auth/tenant-authorization/hooks/use-tenant-authorization"

type PermissionState = {
  canView: boolean
  canEdit: boolean
}

export function useAccessModel() {
  const authorization = useTenantAuthorization()

  return useMemo(() => {
    const isAllowed = (action: TenantAuthorizationAction, requestedBranchSlug?: string) =>
      authorization.routeDecision(action, requestedBranchSlug).status === TenantAuthorizationDecisionStatus.Allowed

    const resolvePermission = (
      module: WorkspaceAccessModule,
      options?: { parish?: boolean },
    ): PermissionState => {
      return {
        canView: isAllowed(
          resolveTenantAuthorizationModuleAction(module, WorkspaceAccessPermission.VIEW),
          options?.parish ? "parish" : undefined,
        ),
        canEdit: isAllowed(
          resolveTenantAuthorizationModuleAction(module, WorkspaceAccessPermission.EDIT),
          options?.parish ? "parish" : undefined,
        ),
      }
    }

    // Parish finance = FINANCE resolved at organization (workspace) scope.
    const parishFinance = resolvePermission(WorkspaceAccessModule.FINANCE, { parish: true })
    // Parish operations = the shared operational modules (GROUPS/MINISTRIES) at organization scope. The
    // old single PARISH_OPERATIONS umbrella is reconstructed as "has any operations access at parish".
    const parishGroups = resolvePermission(WorkspaceAccessModule.GROUPS, { parish: true })
    const parishMinistries = resolvePermission(WorkspaceAccessModule.MINISTRIES, { parish: true })
    const parishOperations: PermissionState = {
      canView: parishGroups.canView || parishMinistries.canView,
      canEdit: parishGroups.canEdit || parishMinistries.canEdit,
    }
    const settings = resolvePermission(WorkspaceAccessModule.SETTINGS)

    // Branch-administration authority for the active branch, derived from assigned roles.
    const branchAdministration: PermissionState = {
      canView: isAllowed(TenantAuthorizationAction.UsersView),
      canEdit: isAllowed(TenantAuthorizationAction.UsersEdit),
    }

    return {
      branch: (module: WorkspaceAccessModule): PermissionState => resolvePermission(module),
      settings,
      branchAdministration,
      parishFinance,
      parishOperations,
      canViewBranchModule: (module: WorkspaceAccessModule) => resolvePermission(module).canView,
      canEditBranchModule: (module: WorkspaceAccessModule) => resolvePermission(module).canEdit,
      hasParishFinanceAccess: parishFinance.canView,
      canViewParishFinance: parishFinance.canView,
      canEditParishFinance: parishFinance.canEdit,
      hasParishOperationsAccess: parishOperations.canView,
      canViewParishOperations: parishOperations.canView,
      canEditParishOperations: parishOperations.canEdit,
    }
  }, [authorization])
}
